/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import {
  Modal,
  Table,
  Descriptions,
  Typography,
} from 'antd';
import { formatFecha, computeRangoInfo } from 'utils/bitacoraHelpers';

const { Text } = Typography;

const fmt2 = (valor) => {
  if (valor === null || valor === undefined) return null;
  const n = Number(String(valor).replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtMXP = (valor) => {
  const v = fmt2(valor);
  return v !== null ? `$${v}` : '—';
};

const ModalDetalleBitacoraGrupo = ({
  visible, onClose, selectedRow, predioInfo,
}) => {
  const records = selectedRow?._records || [];
  const count = selectedRow?._count || 1;
  const esAnual = selectedRow?._esAnual || false;
  const esPeriodo = selectedRow?._esPeriodo || false;
  const esDescuento = selectedRow?.accion_inf === 'APLICAR_DESCUENTO';
  const rep = records[0] || selectedRow || {};

  const descripcion = useMemo(
    () => rep?.data_nueva?.descripcion
      || rep?.data_anterior?.descripcion
      || rep?.data_salida?.descripcion
      || '—',
    [rep],
  );

  const tipoLabel = useMemo(() => {
    if (esAnual) return `${count} año${count !== 1 ? 's' : ''}`;
    if (esPeriodo) return `${count} bimestres`;
    return `${count} período${count !== 1 ? 's' : ''}`;
  }, [count, esAnual, esPeriodo]);
  const usuarioAplico = useMemo(
    () => rep?.data_salida?.usuario_que_aplico
      || rep?.data_salida?.usuario_creo
      || '—',
    [rep],
  );

  const columnsDescuento = useMemo(() => [
    {
      title: 'Comentarios',
      key: 'comentarios',
      width: 110,
      render: (_, row) => row.data_nueva?.comentarios ?? row.data_salida?.comentarios ?? '—',
    },

    {
      title: 'Cantidad desc.',
      key: 'cantidad',
      width: 50,
      render: (_, row) => `% ${row.data_nueva?.variables?.[0]?.valor || 'sin porcentaje'}`,
    },
    {
      title: 'Usuario que aplicó',
      key: 'usuario',
      width: 140,
      render: (_, row) => row.data_salida?.usuario_que_aplico ?? '—',
    },
    {
      title: 'Periodo fiscal',
      key: 'periodo_fiscal',
      width: 110,
      render: (_, row) => row.data_nueva?.infoCargoAplicado?.periodo ?? 'sin periodo fiscal',
    },
    {
      title: 'Periodo',
      key: 'periodo',
      width: 120,
      render: (_, row) => row.data_nueva?.infoCargoAplicado?.descripcion ?? 'sin periodo',
    },
    {
      title: 'Fecha',
      key: 'fecha',
      width: 180,
      render: (_, row) => formatFecha(
        row.data_salida?.fecha_de_aplicacion || row.fecha_de_creacion || row.fecha_hora,
      ),
    },
  ], []);

  // Para cancelaciones: data_anterior trae la info del cargo antes de cancelar
  const columnsCancelacion = useMemo(() => [
    {
      title: 'Descripción cargo',
      key: 'desc',
      width: 220,
      render: (_, row) => row.data_anterior?.descripcion || '—',
    },
    {
      title: 'Importe',
      key: 'importe',
      width: 130,
      render: (_, row) => fmtMXP(row.data_anterior?.importe),
    },
    {
      title: 'Período Fiscal',
      key: 'periodo',
      width: 130,
      render: (_, row) => row.data_anterior?.periodo_fiscal ?? '—',
    },
    {
      title: 'Observaciones',
      key: 'observaciones_de_cancelado',
      width: 100,
      render: (_, row) => row.data_nueva?.observaciones_de_cancelado ?? '—',
    },
    {
      title: 'Fecha cancelación',
      key: 'fecha',
      width: 180,
      render: (_, row) => formatFecha(
        row.data_nueva?.fecha_de_cancelado || row.fecha_de_creacion || row.fecha_hora,
      ),
    },
  ], []);

  const rangoInfo = useMemo(() => computeRangoInfo(records), [records]);

  const sortedRecords = useMemo(() => [...records].sort((a, b) => {
    const infoA = a.data_nueva?.infoCargoAplicado;
    const infoB = b.data_nueva?.infoCargoAplicado;
    if (!infoA && !infoB) return 0;
    if (!infoA) return 1;
    if (!infoB) return -1;
    const periodoA = infoA.periodo || 0;
    const periodoB = infoB.periodo || 0;
    if (periodoA !== periodoB) return periodoA - periodoB;
    const bimestreA = parseInt((infoA.descripcion || '').match(/BIMESTRE\s+(\d+)/i)?.[1] || '0', 10);
    const bimestreB = parseInt((infoB.descripcion || '').match(/BIMESTRE\s+(\d+)/i)?.[1] || '0', 10);
    return bimestreA - bimestreB;
  }), [records]);

  const columns = esDescuento ? columnsDescuento : columnsCancelacion;

  const titulo = esDescuento
    ? `Aplicación de descuentos — ${tipoLabel}`
    : `Cancelación de cargos — ${tipoLabel}`;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      onOk={onClose}
      title={titulo}
      width="90%"
      footer={null}
    >
      {selectedRow && (
        <>
          {predioInfo && (
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 2, md: 3 }}
              style={{ marginBottom: 8 }}
            >
              {predioInfo.propietario && predioInfo.propietario !== '—' && (
                <Descriptions.Item label="Propietario / Partícipes" span={3}>
                  {predioInfo.propietario}
                </Descriptions.Item>
              )}
              {predioInfo.cuentaPredial && (
                <Descriptions.Item label="Cuenta Predial">
                  {predioInfo.cuentaPredial}
                </Descriptions.Item>
              )}
              {predioInfo.cuentaCatastral && (
                <Descriptions.Item label="Cuenta Catastral">
                  {predioInfo.cuentaCatastral}
                </Descriptions.Item>
              )}
              {predioInfo.tipoPredio && (
                <Descriptions.Item label="Tipo de Predio">
                  {predioInfo.tipoPredio}
                </Descriptions.Item>
              )}
              {predioInfo.supTerreno && (
                <Descriptions.Item label="Sup. Terreno">
                  {predioInfo.supTerreno}
                </Descriptions.Item>
              )}
              {predioInfo.supConstruccion && (
                <Descriptions.Item label="Sup. Construcción">
                  {predioInfo.supConstruccion}
                </Descriptions.Item>
              )}
              {predioInfo.valorConstruccion && (
                <Descriptions.Item label="Valor Construcción">
                  {predioInfo.valorConstruccion}
                </Descriptions.Item>
              )}
              {predioInfo.valorFiscal && (
                <Descriptions.Item label="Valor Fiscal">
                  {predioInfo.valorFiscal}
                </Descriptions.Item>
              )}
              {predioInfo.direccion && (
                <Descriptions.Item label="Dirección" span={2}>
                  {predioInfo.direccion}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}

          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Descripción" span={2}>
              {descripcion}
            </Descriptions.Item>
            <Descriptions.Item label="Total Registros">
              <Text strong>{count}</Text>
            </Descriptions.Item>
            {rangoInfo && (
              <Descriptions.Item label="Rango de períodos" span={2}>
                {rangoInfo}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Usuario">
              {usuarioAplico}
            </Descriptions.Item>
          </Descriptions>

          <Table
            dataSource={sortedRecords}
            columns={columns}
            rowKey="id"
            size="small"
            bordered
            pagination={{ pageSize: 20, showSizeChanger: false }}
            scroll={{ x: 'max-content', y: 350 }}
          />
        </>
      )}
    </Modal>
  );
};

export default ModalDetalleBitacoraGrupo;
